import React from "react";
import FadeIn from "./FadeIn";
import BackgroundRings from "./BackgroundRings";
import { waUrl } from "../lib/constants";
import { ArrowRight } from "lucide-react";

const GOLD = "#c9a227";

/**
 * Two-tier animal recovery section.
 * Tier 1 — Equine & Camel Recovery (premium / on-site)
 * Tier 2 — Domestic Companions (in-home dogs / cats / exotic)
 * No prices. CTA → WhatsApp Aditya for context-prefilled enquiry.
 */
export const AnimalRecoverySection = ({ testIdPrefix = "animal-recovery" }) => {
    const tiers = [
        {
            tag: "TIER 01 · ON-SITE",
            title: "Equine & Camel Recovery",
            subline:
                "For racing stables, equestrian clubs, and private estates.",
            body:
                "Sound-based recovery protocols for elite performance animals. " +
                "Resonant frequencies, vibroacoustic and breath-state work delivered " +
                "on-site at your stable or estate, UAE-wide.",
            note:
                "Race camels · Polo horses · Show jumpers · Endurance horses",
            cta: "Enquire for Stables & Estates",
            waMessage:
                "Hi Aditya, I'd like to enquire about Equine / Camel Recovery sessions for our stable / estate.",
            testid: `${testIdPrefix}-tier-equine`,
        },
        {
            tag: "TIER 02 · IN-HOME",
            title: "Domestic Companions",
            subline:
                "For the animal that holds the tension you carry.",
            body:
                "Calm, quiet sound sessions for dogs, cats and exotic companions " +
                "inside your home. Useful around anxiety, post-surgery recovery, " +
                "rescue trauma, and end-of-life comfort.",
            note: "Dogs · Cats · Exotic companions",
            cta: "Enquire for Your Companion",
            waMessage:
                "Hi Aditya, I'd like to enquire about a Domestic Companion session for my dog / cat / pet at home.",
            testid: `${testIdPrefix}-tier-domestic`,
        },
    ];

    return (
        <section
            data-testid={`${testIdPrefix}-section`}
            style={{
                padding: "120px 32px",
                backgroundColor: "#0a0a0a",
                position: "relative",
                borderTop: "1px solid rgba(201,162,39,0.1)",
                overflow: "hidden",
            }}
        >
            <BackgroundRings opacity={0.04} position="left" />
            <div
                style={{
                    maxWidth: 1300,
                    margin: "0 auto",
                    position: "relative",
                    zIndex: 2,
                }}
            >
                <FadeIn>
                    <p className="eyebrow" style={{ marginBottom: 20 }}>
                        Animal Recovery
                    </p>
                    <h2
                        className="font-serif"
                        style={{
                            color: "#fff",
                            fontSize: "clamp(34px, 4.5vw, 56px)",
                            fontWeight: 400,
                            lineHeight: 1.05,
                            letterSpacing: "-0.02em",
                            marginBottom: 24,
                            maxWidth: 820,
                        }}
                        data-testid={`${testIdPrefix}-headline`}
                    >
                        Two tiers.{" "}
                        <span style={{ color: GOLD }}>One science.</span>
                    </h2>
                    <p
                        style={{
                            color: "#bbb",
                            fontSize: 18,
                            lineHeight: 1.7,
                            maxWidth: 720,
                            marginBottom: 64,
                            fontWeight: 300,
                        }}
                    >
                        Performance animals at the estate. Companions at home.
                        The protocol is the same — the nervous system doesn&apos;t
                        care what species it lives inside.
                    </p>
                </FadeIn>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(320px, 1fr))",
                        gap: 32,
                    }}
                >
                    {tiers.map((t, i) => (
                        <FadeIn key={t.title} delay={120 + i * 120}>
                            <div
                                data-testid={t.testid}
                                style={{
                                    border: `1px solid rgba(201,162,39,0.25)`,
                                    backgroundColor:
                                        "rgba(201,162,39,0.025)",
                                    padding: "48px 40px",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <p
                                    style={{
                                        color: GOLD,
                                        fontSize: 10.5,
                                        letterSpacing: "0.32em",
                                        textTransform: "uppercase",
                                        margin: "0 0 24px",
                                        fontWeight: 600,
                                    }}
                                >
                                    {t.tag}
                                </p>
                                <h3
                                    className="font-serif"
                                    style={{
                                        color: "#fff",
                                        fontSize: "clamp(26px, 2.6vw, 34px)",
                                        fontWeight: 400,
                                        lineHeight: 1.15,
                                        letterSpacing: "-0.015em",
                                        margin: "0 0 14px",
                                    }}
                                >
                                    {t.title}
                                </h3>
                                <p
                                    style={{
                                        color: GOLD,
                                        fontSize: 14,
                                        lineHeight: 1.5,
                                        margin: "0 0 22px",
                                        fontWeight: 400,
                                        letterSpacing: "0.01em",
                                        fontStyle: "italic",
                                    }}
                                >
                                    {t.subline}
                                </p>
                                <p
                                    style={{
                                        color: "#bbb",
                                        fontSize: 15,
                                        lineHeight: 1.75,
                                        margin: "0 0 22px",
                                        fontWeight: 300,
                                    }}
                                >
                                    {t.body}
                                </p>
                                <p
                                    style={{
                                        color: "#888",
                                        fontSize: 11.5,
                                        letterSpacing: "0.2em",
                                        textTransform: "uppercase",
                                        margin: "0 0 36px",
                                        fontWeight: 500,
                                    }}
                                >
                                    {t.note}
                                </p>
                                <div style={{ marginTop: "auto" }}>
                                    <a
                                        href={waUrl(t.waMessage)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-gold-solid"
                                        data-testid={`${t.testid}-cta`}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 10,
                                        }}
                                    >
                                        {t.cta}
                                        <ArrowRight size={14} />
                                    </a>
                                </div>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AnimalRecoverySection;
